/**
 * api.js — one function per stroykent_attendance endpoint.
 *
 * Every endpoint is a JSON-RPC `type=json` Odoo route. The wire envelope is:
 *   POST ${VITE_API_BASE}${path}
 *   body: {"jsonrpc":"2.0","method":"call","params":{ ...args, init_data }}
 *   ok  : {"result": { ok:true, ... }}  ->  we return the unwrapped `result`
 *
 * params.init_data is auto-attached from tg.initData on EVERY call — callers
 * never pass it. Business errors already arrive shaped as {ok:false, error}.
 * Transport / JSON-RPC faults are normalized to {ok:false, error:'NETWORK'}
 * (or 'SERVER_ERROR' for a well-formed JSON-RPC error object) so the UI can map
 * a single, predictable shape to the Rejections matrix.
 */
import { initData } from './tg.js';

const API_BASE = (import.meta.env.VITE_API_BASE || '').replace(/\/+$/, '');

/**
 * Shared low-level POST. `args` are merged with init_data into params.
 * Returns the unwrapped JSON-RPC `result`, or a normalized error object.
 */
async function post(path, args = {}) {
  const url = `${API_BASE}${path}`;
  const body = {
    jsonrpc: '2.0',
    method: 'call',
    params: { ...args, init_data: initData },
  };

  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (_e) {
    // DNS / offline / CORS / abort — never surfaces a stack to the worker.
    return { ok: false, error: 'NETWORK' };
  }

  if (!resp.ok) {
    return { ok: false, error: 'NETWORK' };
  }

  let json;
  try {
    json = await resp.json();
  } catch (_e) {
    return { ok: false, error: 'NETWORK' };
  }

  // A well-formed JSON-RPC fault (auth raise on the server, traceback, …).
  if (json.error) {
    return { ok: false, error: 'SERVER_ERROR' };
  }

  const result = json.result;
  if (result === undefined || result === null) {
    return { ok: false, error: 'SERVER_ERROR' };
  }
  return result;
}

// ---- endpoints --------------------------------------------------------------
// Result shapes documented inline; see stroykent_attendance/controllers/main.py.

/** /pair {pin} -> {ok, name, enrolled, consent} | {ok:false, error} */
export function pair(pin) {
  return post('/stroykent/att/pair', { pin });
}

/**
 * /session {} ->
 *   {ok, name, branches:[{id,name}], consent, enrolled,
 *    today:{status:'none'|'in'|'out', check_in, check_out}}
 *   | {ok:false, error:'AUTH_FAILED'|'NOT_PAIRED'}
 */
export function session() {
  return post('/stroykent/att/session', {});
}

/** /consent {} -> {ok, consent:true} */
export function consent() {
  return post('/stroykent/att/consent', {});
}

/** /enroll {image_b64} -> {ok, enrolled:true} | {ok:false, error} */
export function enroll(image_b64) {
  return post('/stroykent/att/enroll', { image_b64 });
}

/**
 * /challenge {} -> {ok, cue:'turn_left'|'turn_right'|'look_up'|'look_down', ttl} | {ok:false,error}
 * Call this RIGHT BEFORE capture; DISPLAY the returned cue. The server ignores
 * any client-chosen cue and consumes this one at checkin/checkout.
 */
export function challenge() {
  return post('/stroykent/att/challenge', {});
}

/**
 * /checkin {lat, lng, accuracy, branch_id?, image_b64, action?, liveness_ok?}
 *   -> {ok, check_in_time:iso} | {ok:false, error}
 * `action` is the OPTIONAL in-app liveness cue the worker performed
 * ('smile'|'wink_left'|'wink_right'|'still'); omitted by the file-input
 * FaceScreen path. `liveness_ok` is the ADVISORY on-device liveness signal (a
 * genuine low→high gesture transition, or micro-motion for 'still'); the server
 * still runs its own face-match + anti-spoof and treats this only as a hint.
 */
export function checkin({ lat, lng, accuracy, branch_id, image_b64, action, liveness_ok }) {
  return post('/stroykent/att/checkin', {
    lat, lng, accuracy, branch_id, image_b64, action, liveness_ok,
  });
}

/**
 * /checkout {lat, lng, accuracy, branch_id?, image_b64, action?, liveness_ok?}
 *   -> {ok, check_out_time:iso} | {ok:false, error}
 */
export function checkout({ lat, lng, accuracy, branch_id, image_b64, action, liveness_ok }) {
  return post('/stroykent/att/checkout', {
    lat, lng, accuracy, branch_id, image_b64, action, liveness_ok,
  });
}

// ---- hybrid browser-capture handoff -----------------------------------------
// The Telegram webview blocks getUserMedia, so punching is delegated to a real
// browser page (/capture). Three routes bridge the two contexts:
//   punch_token   — initData-authed (called from the Mini App); mints a
//                   single-use opaque token bound to {emp,direction,branch}.
//   web_challenge — TOKEN-authed (called from the browser page, NO initData);
//                   issues the liveness cue for the token's employee.
//   web_punch     — TOKEN-authed; consumes the token and runs the SAME punch +
//                   sidecar verify + GPS gate that checkin/checkout use.
// web_* still flow through `post`, so init_data rides along but is empty/ignored
// in the browser (these routes authenticate purely by the token).

/**
 * /punch_token {direction:'in'|'out', branch_id?} -> {ok, token, ttl}
 *   | {ok:false, error}. initData-authed — call from the Mini App only.
 */
export function punchToken({ direction, branch_id }) {
  return post('/stroykent/att/punch_token', { direction, branch_id });
}

/**
 * /web_challenge {token} -> {ok, cue, ttl} | {ok:false, error}
 * TOKEN-authed. Does NOT consume the punch token — only issues the cue.
 */
export function webChallenge(token) {
  return post('/stroykent/att/web_challenge', { token });
}

/**
 * /web_punch {token, lat, lng, accuracy, cue, image_b64}
 *   -> {ok, name, check_in?/check_out?, ...} | {ok:false, error}
 * TOKEN-authed. Consumes the token (single-use) and records the attendance.
 */
export function webPunch({ token, lat, lng, accuracy, cue, image_b64 }) {
  return post('/stroykent/att/web_punch', {
    token, lat, lng, accuracy, cue, image_b64,
  });
}

/**
 * /summary {} -> {ok, status, today_hours, today_hm, live_check_in,
 *                 week_hm, week_days, month_hm, month_days}
 */
export function summary() {
  return post('/stroykent/att/summary', {});
}

/**
 * /history {year?, month?} -> {ok, year, month, month_label, month_hm,
 *   days_present, late_days, early_days, absent_days, min_month,
 *   is_current_month, flags_enabled, days:[{date,day,weekday,weekday_short,
 *   weekday_iso,status,check_in,check_out,worked_hm,branch,late,late_min,
 *   early,early_min}]} | {ok:false, error:'BAD_RANGE'|...}
 */
export function history(year, month) {
  return post('/stroykent/att/history', { year, month });
}

export default {
  post,
  pair,
  session,
  consent,
  enroll,
  challenge,
  checkin,
  checkout,
  summary,
  history,
  punchToken,
  webChallenge,
  webPunch,
};
